import React, { useCallback, useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  IconButton, 
  Checkbox, 
  Paper,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsIcon from '@mui/icons-material/Settings';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import axiosInstance from '../services/axiosInstance';
import { API_BASE_URL } from '../config';
import AdvancedSearchBar, { AdvancedSearchParams } from '../components/AdvancedSearchBar';

interface TrashSettings {
  autoCleanupEnabled: boolean;
  retentionDays: number;
  trashedCount: number;
  expiredCount: number;
}

const getDefaultSearchParams = (): AdvancedSearchParams => ({
  searchTerm: '',
  fileTypes: [],
  fromDate: null,
  toDate: null,
  favorite: null,
  sortBy: 'uploadedAt',
  sortDirection: 'desc',
  searchFields: ['title', 'description', 'tags', 'aiTags', 'aiDescription', 'fileName', 'metadata'],
  recentSearches: [],
  searchOperator: 'OR'
});

const TrashPage: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [items, setItems] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [settings, setSettings] = useState<TrashSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<AdvancedSearchParams>(getDefaultSearchParams());

  const loadItems = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams();

      if (searchParams.searchTerm.trim()) {
        queryParams.append('search', searchParams.searchTerm);
        if (searchParams.searchOperator) {
          queryParams.append('operator', searchParams.searchOperator);
        }
      }
      if (searchParams.fileTypes.length > 0) {
        queryParams.append('fileTypes', searchParams.fileTypes.join(','));
      }
      if (searchParams.fromDate) {
        queryParams.append('fromDate', searchParams.fromDate.toISOString());
      }
      if (searchParams.toDate) {
        queryParams.append('toDate', searchParams.toDate.toISOString());
      }
      if (searchParams.favorite !== null) {
        queryParams.append('favorite', searchParams.favorite.toString());
      }
      if (searchParams.searchFields.length > 0) {
        queryParams.append('searchFields', searchParams.searchFields.join(','));
      }
      if (searchParams.sortBy) {
        queryParams.append('sortBy', searchParams.sortBy);
        queryParams.append('sortDirection', searchParams.sortDirection);
      }

      // Keep search scoped strictly to trash.
      queryParams.append('trashed', 'true');

      const resp = await axiosInstance.get('/api/assets', { params: Object.fromEntries(queryParams) });
      setItems(resp.data || []);
      setSelectedIds(new Set()); // Clear selection after reload
    } catch (e: any) {
      setError('Kunde inte ladda soptunnan');
    }
  }, [searchParams]);

  const loadSettings = async () => {
    setSettingsLoading(true);
    try {
      const resp = await axiosInstance.get('/api/trash/settings');
      setSettings(resp.data);
    } catch (e: any) {
      console.error('Error loading trash settings:', e);
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSearch = useCallback((params: AdvancedSearchParams) => {
    setSearchParams(params);
  }, []);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(it => it._id)));
    }
  };

  const restore = async (id: string) => {
    try {
      await axiosInstance.post('/api/trash/restore', { assetIds: [id] });
      setSuccess('Filen har återställts');
      await loadItems();
      loadSettings();
    } catch (e: any) {
      setError('Kunde inte återställa filen');
    }
  };

  const restoreSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Vill du återställa ${selectedIds.size} fil(er)?`)) return;
    
    setIsProcessing(true);
    try {
      await axiosInstance.post('/api/trash/restore', { assetIds: Array.from(selectedIds) });
      setSuccess(`${selectedIds.size} fil(er) har återställts`);
      await loadItems();
      loadSettings();
    } catch (e: any) {
      setError('Kunde inte återställa filerna');
    } finally {
      setIsProcessing(false);
    }
  };

  const purge = async (id: string) => {
    if (!window.confirm('Vill du radera denna fil PERMANENT? Detta går inte att ångra.')) return;
    try {
      await axiosInstance.post('/api/trash/purge', { assetIds: [id] });
      setSuccess('Filen har raderats permanent');
      await loadItems();
      loadSettings();
    } catch (e: any) {
      setError('Kunde inte radera filen permanent');
    }
  };

  const purgeSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Vill du radera ${selectedIds.size} fil(er) PERMANENT? Detta går inte att ångra!`)) return;
    
    setIsProcessing(true);
    try {
      await axiosInstance.post('/api/trash/purge', { assetIds: Array.from(selectedIds) });
      setSuccess(`${selectedIds.size} fil(er) har raderats permanent`);
      await loadItems();
      loadSettings();
    } catch (e: any) {
      setError('Kunde inte radera filerna permanent');
    } finally {
      setIsProcessing(false);
    }
  };

  const purgeExpired = async () => {
    if (!settings) return;
    if (!window.confirm(`Vill du radera alla filer som varit i soptunnan längre än ${settings.retentionDays} dagar? Detta går inte att ångra!`)) return;
    
    setIsProcessing(true);
    try {
      const resp = await axiosInstance.post('/api/trash/purge-expired');
      setSuccess(resp.data.message || 'Utgångna filer har raderats');
      await loadItems();
      loadSettings();
    } catch (e: any) {
      setError('Kunde inte radera utgångna filer');
    } finally {
      setIsProcessing(false);
    }
  };

  const updateSettings = async (newSettings: Partial<TrashSettings>) => {
    try {
      const resp = await axiosInstance.put('/api/trash/settings', newSettings);
      setSettings(prev => prev ? { ...prev, ...resp.data.settings } : null);
      setSuccess('Inställningar sparade');
    } catch (e: any) {
      setError('Kunde inte spara inställningar');
    }
  };

  // Clear messages after a few seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 2, position: 'relative' }}>
      <IconButton aria-label="Stäng" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, pr: 5 }}>
        <Typography variant="h5">Soptunna</Typography>
        <Tooltip title="Inställningar">
          <IconButton onClick={() => setShowSettings(!showSettings)} color={showSettings ? 'primary' : 'default'}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Messages */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ mb: 2 }}>
        <AdvancedSearchBar onSearch={handleSearch} initialParams={searchParams} />
      </Box>

      {/* Settings panel */}
      {showSettings && (
        <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon fontSize="small" />
            Soptunne-inställningar
          </Typography>
          
          {settingsLoading ? (
            <CircularProgress size={24} />
          ) : settings ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoCleanupEnabled}
                      onChange={(e) => updateSettings({ autoCleanupEnabled: e.target.checked })}
                    />
                  }
                  label="Auto-rensning aktiverad"
                />
                
                <FormControl size="small" sx={{ minWidth: 150 }} disabled={!settings.autoCleanupEnabled}>
                  <InputLabel>Behåll i</InputLabel>
                  <Select
                    value={settings.retentionDays}
                    label="Behåll i"
                    onChange={(e) => updateSettings({ retentionDays: Number(e.target.value) })}
                  >
                    <MenuItem value={7}>7 dagar</MenuItem>
                    <MenuItem value={14}>14 dagar</MenuItem>
                    <MenuItem value={30}>30 dagar</MenuItem>
                    <MenuItem value={60}>60 dagar</MenuItem>
                    <MenuItem value={90}>90 dagar</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Divider />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Totalt i soptunnan: <strong>{settings.trashedCount}</strong> filer
                </Typography>
                {settings.autoCleanupEnabled && settings.expiredCount > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Utgångna (äldre än {settings.retentionDays} dagar): <strong>{settings.expiredCount}</strong> filer
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<CleaningServicesIcon />}
                      onClick={purgeExpired}
                      disabled={isProcessing}
                    >
                      Rensa utgångna nu
                    </Button>
                  </>
                )}
              </Box>
              
              <Typography variant="caption" color="text.secondary">
                {settings.autoCleanupEnabled 
                  ? `Filer som varit i soptunnan längre än ${settings.retentionDays} dagar raderas automatiskt varje dag.`
                  : 'Auto-rensning är inaktiverad. Filer måste raderas manuellt.'
                }
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary">Kunde inte ladda inställningar</Typography>
          )}
        </Paper>
      )}
      
      {/* Multi-select toolbar */}
      {items.length > 0 && (
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 2, 
            p: 1.5, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: '8px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={selectedIds.size === items.length && items.length > 0}
              indeterminate={selectedIds.size > 0 && selectedIds.size < items.length}
              onChange={selectAll}
              disabled={isProcessing}
            />
            <Typography variant="body2" sx={{ ml: 1 }}>
              {selectedIds.size > 0 
                ? `${selectedIds.size} av ${items.length} markerade`
                : `Markera alla (${items.length})`
              }
            </Typography>
          </Box>
          
          {selectedIds.size > 0 && (
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<RestoreIcon />}
                onClick={restoreSelected}
                disabled={isProcessing}
              >
                Återställ ({selectedIds.size})
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={purgeSelected}
                disabled={isProcessing}
              >
                Radera permanent ({selectedIds.size})
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f9f9f9' }}>
          <DeleteForeverIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Soptunnan är tom
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Filer som raderas hamnar här och kan återställas eller tas bort permanent.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(auto-fill, minmax(200px, 1fr))',
            sm: 'repeat(auto-fill, minmax(220px, 1fr))',
            md: 'repeat(auto-fill, minmax(240px, 1fr))'
          }
        }}>
          {items.map((it: any) => (
            <Box key={it._id}>
              <Card 
                sx={{ 
                  border: selectedIds.has(it._id) ? '2px solid #1976d2' : '1px solid #d4d4d4',
                  borderRadius: '8px',
                  boxShadow: selectedIds.has(it._id) ? '0 0 8px rgba(25, 118, 210, 0.3)' : 'none',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    transition: 'transform 0.2s ease'
                  },
                  position: 'relative'
                }}
              >
                {/* Checkbox overlay */}
                <Checkbox
                  checked={selectedIds.has(it._id)}
                  onChange={() => toggleSelect(it._id)}
                  disabled={isProcessing}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    zIndex: 1,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)'
                    }
                  }}
                />
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleSelect(it._id)}
                >
                  <img
                    src={it.previewPaths?.thumbnail ? `${API_BASE_URL}${it.previewPaths.thumbnail}` : `${API_BASE_URL}/${(it.filePath||'').replace(/\\/g,'/')}`}
                    alt={it.title || it.fileName}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}
                  >
                    {it.title || it.fileName}
                  </Typography>
                  {it.trashedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Raderad: {formatDate(it.trashedAt)}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ p: 1, pt: 0, gap: 0.5 }}>
                  <Button 
                    size="small" 
                    onClick={() => restore(it._id)}
                    variant="outlined"
                    sx={{ flex: 1 }}
                    disabled={isProcessing}
                  >
                    Återställ
                  </Button>
                  <Button 
                    size="small" 
                    color="error" 
                    onClick={() => purge(it._id)}
                    variant="outlined"
                    sx={{ flex: 1 }}
                    disabled={isProcessing}
                  >
                    Radera
                  </Button>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>
      )}
      <Button sx={{ mt: 2 }} onClick={onClose}>Stäng</Button>
    </Box>
  );
};

export default TrashPage;
